import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  PromptInput,
  Textarea,
  TextareaProps,
} from "@amzn/awsui-components-react";
import { PromptInputProps } from "@amzn/awsui-components-react/polaris/prompt-input";
import { Stage } from "../../lusca-search-api/generated-src";
import { useInvestigationAssignees } from "../../hooks/useInvestigations";
import { CommentMarkdown } from "./CommentMarkdown";
import { extractMentions } from "./mentions";
import { MentionChip } from "./MentionChip";
import { MENTION_MENU_ID, textToTokens, tokensToText } from "./mentionTokens";
import "./CommentInput.scss";

interface BaseCommentInputProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: TextareaProps["onKeyDown"];
  placeholder?: string;
  rows?: number;
  ariaLabel: string;
}

interface MentionProps {
  /** Enables @mentions: Lusca Search users of this stage are offered after `@`. */
  stage: Stage;
  /** The author: never listed as someone a mention will notify. */
  currentUser?: string | null;
}

type CommentInputProps = BaseCommentInputProps & Partial<MentionProps>;

const NO_ALIASES: readonly string[] = [];

/** Preview beside the input, rendered by the same CommentMarkdown the posted comment uses. */
const Preview: React.FC<{ value: string; mentions?: readonly string[] }> = ({
  value,
  mentions,
}) => (
  <div className="comment-input__preview-frame">
    {/* On the border, like a fieldset legend, so the text lines up with the input's. */}
    <span className="comment-input__preview-label">Preview</span>
    <div className="comment-input__preview" data-testid="comment-preview">
      {value.trim() ? (
        <CommentMarkdown text={value} mentions={mentions} />
      ) : (
        <Box color="text-status-inactive" fontSize="body-s">
          Nothing to preview yet.
        </Box>
      )}
    </div>
  </div>
);

/** Plain textarea (no stage: mentions not available). */
const PlainCommentInput: React.FC<BaseCommentInputProps> = ({
  value,
  onChange,
  onKeyDown,
  placeholder,
  rows = 2,
  ariaLabel,
}) => (
  <div className="comment-input comment-input--split">
    <Textarea
      value={value}
      onChange={({ detail }) => onChange(detail.value)}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      rows={Math.max(rows, 4)}
      ariaLabel={ariaLabel}
    />
    <Preview value={value} />
  </div>
);

/**
 * Comment box with @mentions, on Cloudscape PromptInput's `@` menu: typing `@` opens a filtered,
 * scrollable list of Lusca Search users at the caret and a pick becomes a mention token. The
 * comment itself stays plain text (`@alias`), which is what the backend parses and validates.
 *
 * Keys: Enter adds a line (PromptInput would submit; we turn its action into a line break),
 * ⌘/Ctrl+Enter posts via the caller's onKeyDown, Enter/Tab in the open menu picks.
 */
const MentionCommentInput: React.FC<BaseCommentInputProps & MentionProps> = ({
  value,
  onChange,
  onKeyDown,
  placeholder,
  rows = 2,
  ariaLabel,
  stage,
  currentUser,
}) => {
  const input = useRef<PromptInputProps.Ref>(null);
  const modifierEnter = useRef(false);
  const assignees = useInvestigationAssignees(stage, value.includes("@"));
  const aliases = assignees?.data?.aliases ?? NO_ALIASES;
  const known = useMemo(() => new Set(aliases), [aliases]);

  // Tokens are the source of truth while typing; text set from outside (draft restore, pasted
  // images, clear after posting) is turned back into tokens.
  const [tokens, setTokens] = useState<readonly PromptInputProps.InputToken[]>(
    () => textToTokens(value, known)
  );
  // Re-tokenize when the text changes from outside, or when the alias list arrives (an @alias
  // typed before it loaded becomes a mention chip without another keystroke). The (value, known)
  // pair the tokens were built from is remembered, so a value that does not round-trip exactly
  // (e.g. "@Bssas" becoming a lower-case chip) is tokenized once, not on every render.
  const [synced, setSynced] = useState({ value, known });
  if (synced.value !== value || synced.known !== known) {
    setSynced({ value, known });
    setTokens(textToTokens(value, known));
  }

  const menus = useMemo<PromptInputProps.MenuDefinition[]>(
    () => [
      {
        id: MENTION_MENU_ID,
        trigger: "@",
        options: aliases.map((alias) => ({ value: alias, label: alias })),
        statusType: assignees?.isLoading ? "loading" : "finished",
        empty: "No Lusca Search user matches",
      },
    ],
    [aliases, assignees?.isLoading]
  );

  const mentioned = useMemo(
    () => extractMentions(value).filter((a) => known.has(a)),
    [value, known]
  );
  const notified = mentioned.filter((a) => a !== currentUser);

  return (
    <div className="comment-input comment-input--split">
      <div className="comment-input__editor">
        <PromptInput
          ref={input}
          tokens={tokens}
          tokensToText={tokensToText}
          onChange={({ detail }) => {
            const next = detail.tokens ?? [];
            const text = tokensToText(next);
            setTokens(next);
            setSynced({ value: text, known });
            onChange(text);
          }}
          onKeyDown={(event) => {
            modifierEnter.current =
              event.detail.key === "Enter" &&
              (event.detail.metaKey || event.detail.ctrlKey);
            onKeyDown?.(event);
          }}
          onAction={() => {
            // PromptInput treats a plain Enter as "send"; in a comment it starts a new line.
            if (!modifierEnter.current) input.current?.insertText("\n");
          }}
          menus={menus}
          maxMenuHeight={240}
          minRows={Math.max(rows, 4)}
          maxRows={12}
          placeholder={placeholder}
          ariaLabel={ariaLabel}
          i18nStrings={{ menuLoadingText: "Loading people…" }}
        />
      </div>
      <Preview value={value} mentions={mentioned} />
      {notified.length > 0 && (
        <div className="comment-input__notify" data-testid="mention-notify">
          <Box color="text-body-secondary" fontSize="body-s">
            Will notify and add as contributors:{" "}
            {notified.map((alias, i) => (
              <React.Fragment key={alias}>
                {i > 0 && ", "}
                <MentionChip alias={alias} />
              </React.Fragment>
            ))}
          </Box>
        </div>
      )}
    </div>
  );
};

export const CommentInput: React.FC<CommentInputProps> = ({
  stage,
  currentUser,
  ...rest
}) =>
  stage ? (
    <MentionCommentInput {...rest} stage={stage} currentUser={currentUser} />
  ) : (
    <PlainCommentInput {...rest} />
  );

export default CommentInput;
